import apiClient from './apiClient';

export interface CurrentLicense {
  dueTime: string | null;
  status: 'active';
  /** Wire value of the server `LicenseType` enum (0 = Standard, 1 = Floating, …). */
  type: number;
  /** True when the resolved runtime license is demo-classified. */
  isDemo: boolean;
  /** Remaining exportable Revit elements; `null` means unlimited. */
  remainingExportElements: number | null;
}

const isValidDueTime = (value: unknown): value is string | null =>
  value === null || (typeof value === 'string' && !Number.isNaN(Date.parse(value)));

const isValidLicenseType = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0;

const isValidRemainingExportElements = (value: unknown): value is number | null =>
  value === null || (typeof value === 'number' && Number.isInteger(value) && value >= 0);

/** Loads non-sensitive license status for the authenticated user. */
export async function fetchCurrentLicense(): Promise<CurrentLicense> {
  const response = await apiClient.get<unknown>('/licenses/current');
  const data = response.data;

  if (
    !data ||
    typeof data !== 'object' ||
    !('dueTime' in data) ||
    !isValidDueTime(data.dueTime) ||
    !('status' in data) ||
    data.status !== 'active' ||
    !('type' in data) ||
    !isValidLicenseType(data.type) ||
    !('isDemo' in data) ||
    typeof data.isDemo !== 'boolean' ||
    !('remainingExportElements' in data) ||
    !isValidRemainingExportElements(data.remainingExportElements)
  ) {
    throw new Error('Current license response was invalid');
  }

  return {
    dueTime: data.dueTime,
    status: data.status,
    type: data.type,
    isDemo: data.isDemo,
    remainingExportElements: data.remainingExportElements,
  };
}
